import {
  AuthInvalidJwtError,
  isAuthApiError,
  isAuthSessionMissingError,
} from '@supabase/supabase-js'
import type { User } from '@supabase/supabase-js'

import { getSigningKeys, type Jwks } from './jwks'

const AUTH_CHECK_RETRY_DELAY_MS = 150

export type SupabaseAuth = {
  getUser: () => Promise<{
    data: { user: User | null }
    error: unknown
  }>
}

/**
 * Whether the auth check produced an answer at all, and if so which one.
 *
 * `indeterminate` is the important case: it means the check itself failed
 * (Supabase unreachable, 5xx, unexpected throw), which is *not* evidence
 * that the caller is logged out. Every caller has to decide what to do with
 * that separately from "definitely logged out" — see `updateSession` (serve
 * the page anyway) and `createContext` (fail with a retryable error).
 */
export type AuthCheckResult<TUser> =
  | { kind: 'authenticated'; user: TUser }
  | { kind: 'unauthenticated' }
  | { kind: 'indeterminate'; reason: string }

/** The result of a `getUser()`-based check — see `checkAuth`. */
export type AuthCheck = AuthCheckResult<User>

const describeError = (error: unknown): string =>
  error instanceof Error ? `${error.name}: ${error.message}` : String(error)

/**
 * GoTrue statuses that actually mean "these credentials are not valid".
 *
 * An allowlist, not a 4xx range: 429 (rate limited) and 408 (request timeout)
 * are 4xx but are not credential rejections, and GoTrue does rate-limit — so a
 * range would log an authenticated session out under exactly the concurrent
 * load this code exists to survive. Anything not listed here is read as "we
 * did not find out", which is the safe direction.
 */
const CREDENTIALS_REJECTED_STATUSES = new Set([400, 401, 403, 404])

const isDefinitiveAuthFailure = (error: unknown): boolean => {
  if (isAuthSessionMissingError(error)) return true
  // Sibling classes, not subclasses: a transport failure arrives as
  // AuthRetryableFetchError, which `isAuthApiError` (a `name` check) does not
  // match, so it falls through to indeterminate as intended.
  if (isAuthApiError(error))
    return (
      error.status !== undefined &&
      CREDENTIALS_REJECTED_STATUSES.has(error.status)
    )
  return false
}

/**
 * Verifies the session by asking GoTrue about it.
 *
 * The client-side path (`useUser`), where the browser holds one long-lived
 * Supabase client and asks once. Server code wants `verifySession` instead — it
 * verifies locally and does not put a GoTrue request behind every application
 * request.
 */
export async function checkAuth(auth: SupabaseAuth): Promise<AuthCheck> {
  try {
    const { data, error } = await auth.getUser()
    if (error) {
      if (isDefinitiveAuthFailure(error)) return { kind: 'unauthenticated' }
      return { kind: 'indeterminate', reason: describeError(error) }
    }
    return data.user
      ? { kind: 'authenticated', user: data.user }
      : { kind: 'unauthenticated' }
  } catch (error) {
    if (isDefinitiveAuthFailure(error)) return { kind: 'unauthenticated' }
    return { kind: 'indeterminate', reason: describeError(error) }
  }
}

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Runs the auth check, retrying while it could not complete.
 *
 * An unreachable GoTrue surfaces as `TIMEOUT`/408 to the caller, which is only
 * partly recoverable client-side: mutations retry it
 * (`shouldRetryOnlyOnSessionTimeout`), but a hiccup lasting longer than React
 * Query's 1s + 2s + 4s backoff exhausts those too. CI has shown exactly that —
 * `assumptionSet.deleteAssumptionSet never landed — last answer was 408` with
 * every attempt answering 408 (KBE-770).
 *
 * Retrying here is the cheaper place to absorb it: a couple of short waits on
 * the *failure* path only, well under the client's own backoff, and invisible on
 * the happy path. The delays are bounded and few so a genuinely down GoTrue
 * still fails fast rather than holding the request open.
 *
 * Worth knowing what this does *not* cover: when the underlying failure was a
 * token refresh, auth-js caches the failure against the refresh token for a
 * cool-down period, so a further call on the same client returns the cached
 * error without touching the network. The retries only genuinely re-fire for a
 * failed `/user` read — which is the case being seen. It is a cheap win for the
 * common blip, not a guarantee: the classification above is what actually
 * protects the session, and what removed the load itself was `verifySession`,
 * which stops making GoTrue validate every single request.
 */
const AUTH_CHECK_RETRY_DELAYS_MS = [
  AUTH_CHECK_RETRY_DELAY_MS,
  AUTH_CHECK_RETRY_DELAY_MS * 3,
]

export async function checkAuthWithRetry(
  auth: SupabaseAuth,
  retryDelayMs?: number,
): Promise<AuthCheck> {
  const delays =
    retryDelayMs === undefined
      ? AUTH_CHECK_RETRY_DELAYS_MS
      : AUTH_CHECK_RETRY_DELAYS_MS.map(() => retryDelayMs)

  let result = await checkAuth(auth)
  for (const retryDelay of delays) {
    if (result.kind !== 'indeterminate') return result
    await delay(retryDelay)
    result = await checkAuth(auth)
  }
  return result
}

/** What a server request needs to know about its caller. */
export type SessionUser = { id: string; email: string | undefined }

export type SessionCheck = AuthCheckResult<SessionUser>

type JwtClaims = { sub?: string; email?: string; client_id?: unknown }

export type SupabaseClaimsAuth = {
  getClaims: (
    jwt?: string,
    options?: { jwks?: Jwks },
  ) => Promise<{
    data: { claims: JwtClaims } | null
    error: unknown
  }>
}

/**
 * Narrower than `CREDENTIALS_REJECTED_STATUSES` by one status: 404.
 *
 * On the `getUser` path a 404 means "no such user", which is a real rejection.
 * Here the same status could instead come from a project whose JWKS endpoint
 * isn't there, and reading *that* as "logged out" would sign everyone out over
 * a misconfiguration. Indeterminate is the recoverable direction.
 */
const SESSION_REJECTED_STATUSES = new Set([400, 401, 403])

const isDefinitiveSessionFailure = (error: unknown): boolean => {
  if (isAuthSessionMissingError(error)) return true
  // Malformed, expired, or wrongly-signed token. `getClaims` refreshes a
  // near-expiry session before validating, so reaching this really does mean the
  // caller has no usable token. Only one copy of auth-js is installed (the
  // `overrides` pin), so `instanceof` is reliable across the supabase-js
  // re-export.
  if (error instanceof AuthInvalidJwtError) return true
  if (isAuthApiError(error))
    return (
      error.status !== undefined && SESSION_REJECTED_STATUSES.has(error.status)
    )
  return false
}

/**
 * Verifies the caller's session, locally where the project allows it.
 *
 * This is the server-side counterpart to `checkAuth`, and the difference is the
 * whole point. `getUser()` asks GoTrue about every single request, so the auth
 * service takes one HTTP request per application request — measured at a
 * sustained 1,400–2,000/min during an e2e run at two Playwright workers, and
 * the proxy plus the tRPC context each made their own call. At that volume the
 * occasional dropped connection to Kong became a steady stream of
 * `AuthRetryableFetchError` → `TIMEOUT`/408 failures: the dominant residual
 * cause of e2e flakiness, and a researcher-facing risk since a lost mutation
 * shows only an "Action failed" toast (KBE-770).
 *
 * `getClaims()` is Supabase's own answer to that ("Prefer this method over
 * getUser which always sends a request to the Auth server for each JWT"): with
 * asymmetric signing keys it verifies the token's signature locally via
 * WebCrypto against the project's JWKS, which `./jwks` caches per process. Local
 * Supabase already issues ES256 tokens, so this removes the per-request round
 * trip outright rather than making it more reliable.
 *
 * The tradeoff Supabase accepts along with it: a session revoked mid-life is
 * honoured until the access token expires, because nothing asks the server any
 * more. For a hosted project still on the legacy shared JWT secret there is no
 * tradeoff and no gain — auth-js falls back to a `getUser()` call internally, so
 * behavior is exactly what it was before.
 */
export async function verifySession(
  auth: SupabaseClaimsAuth,
  jwks?: Jwks,
): Promise<SessionCheck> {
  try {
    const keys = jwks ?? (await getSigningKeys())
    const { data, error } = await auth.getClaims(
      undefined,
      keys ? { jwks: keys } : undefined,
    )
    if (error) {
      if (isDefinitiveSessionFailure(error)) return { kind: 'unauthenticated' }
      return { kind: 'indeterminate', reason: describeError(error) }
    }
    // No session at all: `getClaims` answers with both fields null.
    if (!data) return { kind: 'unauthenticated' }
    const { sub, email, client_id: oauthClientId } = data.claims
    // An access token Supabase issued to an MCP client a researcher approved
    // (KBE-954) is signed like a browser session and carries the same `aud`.
    // It is good for /api/mcp only: accepted here, an approved app could set
    // it as the session cookie and use the whole web app as the researcher,
    // past the Marvin-copy gate, and keep doing so after a Disconnect until
    // the token expired.
    if (oauthClientId !== undefined) return { kind: 'unauthenticated' }
    return sub
      ? { kind: 'authenticated', user: { id: sub, email } }
      : { kind: 'unauthenticated' }
  } catch (error) {
    if (isDefinitiveSessionFailure(error)) return { kind: 'unauthenticated' }
    return { kind: 'indeterminate', reason: describeError(error) }
  }
}

/**
 * `verifySession`, retrying while it could not complete — see
 * `checkAuthWithRetry` for why the retries are here and not only client-side.
 *
 * Still worth keeping now that verification is usually local: a cold process
 * has to fetch the JWKS, and a near-expiry session still needs a token refresh
 * against GoTrue, so there remain network steps that can blip.
 */
export async function verifySessionWithRetry(
  auth: SupabaseClaimsAuth,
  retryDelayMs?: number,
): Promise<SessionCheck> {
  const delays =
    retryDelayMs === undefined
      ? AUTH_CHECK_RETRY_DELAYS_MS
      : AUTH_CHECK_RETRY_DELAYS_MS.map(() => retryDelayMs)

  let result = await verifySession(auth)
  for (const retryDelay of delays) {
    if (result.kind !== 'indeterminate') return result
    await delay(retryDelay)
    result = await verifySession(auth)
  }
  return result
}
